import React, { useEffect, useState, useMemo } from 'react'; // wrapped by AdminLayout
import { Container, Typography, Box, Paper, TextField, Button, Grid, Divider, CircularProgress, IconButton, Alert, Tooltip, Chip, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Save, Add, Delete, RestartAlt, CheckCircle, Edit } from '@mui/icons-material';
import { shippingService } from '../../services/api';
import { toast } from 'react-toastify';

const AdminShipping = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [subtotal, setSubtotal] = useState('');
  const [computedQuote, setComputedQuote] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [editingOptionIndex, setEditingOptionIndex] = useState(-1);
  const [optionForm, setOptionForm] = useState({ id: '', name: '', price: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await shippingService.getSettings();
        let data = res.data;
        // Fallback migration: if legacy structure present convert to a single option if needed
        if(!data.options){
          const legacyBase = data.standard?.basePrice ?? 0;
            data.options = [ { id: 'standard', name: 'Standard Shipping', price: legacyBase } ];
        }
        // Strip legacy keys from local editing context to avoid re-saving them
        delete data.standard;
        setSettings(data);
        setOriginalSettings(data);
      } catch (e) {
        toast.error('Failed to load shipping settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isDirty = useMemo(()=>{
    if(!settings || !originalSettings) return false;
    try { return JSON.stringify(settings) !== JSON.stringify(originalSettings); } catch { return true; }
  }, [settings, originalSettings]);

  const validate = (cfg) => {
    const errs = [];
    if(!cfg) return ['No settings loaded'];
    if(!Array.isArray(cfg.options) || cfg.options.length === 0) errs.push('At least one shipping option required');
    (cfg.options||[]).forEach((o,i)=>{
      if(!o.id) errs.push(`Option ${i+1} missing id`);
      if(!o.name) errs.push(`Option ${i+1} missing name`);
      if(o.price == null || o.price < 0) errs.push(`Option ${i+1} price must be >= 0`);
    });
  if(cfg.freeShippingThreshold != null && cfg.freeShippingThreshold < 0) errs.push('Free shipping threshold must be >= 0 or blank');
  if(cfg.codExtraCharge != null && cfg.codExtraCharge < 0) errs.push('COD extra charge must be >= 0 or blank');
    return errs;
  };

  useEffect(()=>{ if(settings) setValidationErrors(validate(settings)); }, [settings]);

  const resetChanges = () => { if(originalSettings){ setSettings(originalSettings); setEditing(false);} };

  const saveSettings = async () => {
    const errs = validate(settings);
    setValidationErrors(errs);
    if(errs.length){ toast.error('Fix validation errors'); return; }
    try {
      setSaving(true);
  const payload = { options: settings.options, freeShippingThreshold: settings.freeShippingThreshold ?? null, codExtraCharge: settings.codExtraCharge ?? null };
      await shippingService.updateSettings(payload);
      toast.success('Settings saved');
      setOriginalSettings(payload);
      setEditing(false);
    } catch(e){
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const requestQuote = async () => {
    try {
      setQuoteLoading(true);
      const payload = { subtotal: parseFloat(subtotal) || 0 };
      const res = await shippingService.getQuote(payload);
      setComputedQuote(res.data);
    } catch (e) {
      toast.error('Quote failed');
    } finally {
      setQuoteLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingOptionIndex(-1);
    setOptionForm({ id:'', name:'', price:'' });
    setOptionModalOpen(true);
  };

  const saveOption = () => {
    const priceNum = parseFloat(optionForm.price);
    if(!optionForm.id){
      // auto-generate id from name if possible
      if(optionForm.name) optionForm.id = optionForm.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
    }
    if(!optionForm.id || !optionForm.name || isNaN(priceNum) || priceNum < 0){ toast.error('Provide valid id, name and non-negative price'); return; }
    if(editingOptionIndex === -1 && settings.options.some(o=>o.id===optionForm.id)){ toast.error('ID already exists'); return; }
    setSettings(s=>{
      const opts = [...(s.options||[])];
      if(editingOptionIndex > -1){
        opts[editingOptionIndex] = { ...opts[editingOptionIndex], name: optionForm.name, price: priceNum };
      } else {
        opts.push({ id: optionForm.id, name: optionForm.name, price: priceNum });
      }
      return { ...s, options: opts };
    });
    setOptionModalOpen(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Container maxWidth="md" sx={{ py:4 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Shipping Management {(!editing && isDirty) && <Chip size="small" color="warning" label="Unsaved" sx={{ ml:1 }} />}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Manage your fixed shipping options. Customers will choose from these during checkout. Set a free shipping threshold to automatically make all options free above a subtotal.
  You can also set an extra charge for Cash on Delivery (COD) orders.
      </Typography>

      <Paper sx={{ p:3, mb:4 }} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontWeight={600}>Settings</Typography>
          <Box display="flex" gap={1}>
            {editing && (
              <Tooltip title="Reset local changes">
                <span>
                  <IconButton size="small" onClick={resetChanges} disabled={!isDirty}>
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Button size="small" onClick={()=>setEditing(e=>!e)}>{editing? 'Close':'Edit'}</Button>
          </Box>
        </Box>
        {!!validationErrors.length && editing && (
          <Alert severity="error" sx={{ mb:2 }}>
            {validationErrors.map((er,i)=><div key={i}>• {er}</div>)}
          </Alert>
        )}
        {settings && !editing && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Free Shipping Threshold: {settings.freeShippingThreshold != null ? `₹${settings.freeShippingThreshold}` : 'Not set'}</Typography>
            <Typography variant="subtitle2" gutterBottom>Options:</Typography>
            <Table size="small" sx={{ mb:1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.options?.map((o,i)=>(
                  <TableRow key={i}>
                    <TableCell>{o.name}</TableCell>
                    <TableCell>{o.price}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {settings.freeShippingThreshold != null && (
              <Typography variant="caption" color="text.secondary">Subtotal ≥ ₹{settings.freeShippingThreshold} applies free shipping to all options.</Typography>
            )}
          </Box>
        )}
        {editing && settings && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Free Shipping Threshold (₹)" type="number" value={settings.freeShippingThreshold ?? ''} onChange={e=>setSettings(s=>({...s, freeShippingThreshold: e.target.value===''? null: parseFloat(e.target.value)}))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="COD Extra Charge (₹)" type="number" value={settings.codExtraCharge ?? ''} onChange={e=>setSettings(s=>({...s, codExtraCharge: e.target.value===''? null: parseFloat(e.target.value)}))} />
              </Grid>
              <Grid item xs={12} sm={4} display="flex" justifyContent="flex-end" alignItems="center">
                <Button size="small" startIcon={<Add />} onClick={openAddModal}>Add Option</Button>
              </Grid>
            </Grid>
            <Divider sx={{ my:2 }} />
            <Typography variant="subtitle2" gutterBottom>Shipping Options</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Price (₹)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.options?.map((opt, idx)=>(
                  <TableRow key={opt.id} hover>
                    <TableCell>{opt.id}</TableCell>
                    <TableCell>{opt.name}</TableCell>
                    <TableCell>{opt.price}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={()=>{ setEditingOptionIndex(idx); setOptionForm({ id: opt.id, name: opt.name, price: String(opt.price) }); setOptionModalOpen(true); }}><Edit fontSize="inherit" /></IconButton>
                      <IconButton size="small" onClick={()=> setSettings(s=> ({ ...s, options: s.options.filter((_,i)=>i!==idx) })) } disabled={settings.options.length<=1}><Delete fontSize="inherit" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box mt={2}>
              <Button variant="contained" startIcon={<Save />} onClick={saveSettings} disabled={saving || !!validationErrors.length || !isDirty}>{saving? 'Saving...':'Save Settings'}</Button>
              <Button variant="text" size="small" sx={{ ml:1 }} onClick={resetChanges} disabled={!isDirty}>Reset</Button>
              {!validationErrors.length && isDirty && <Chip size="small" color="info" icon={<CheckCircle />} label="Ready" sx={{ ml:1 }} />}
            </Box>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p:3, mb:4 }} variant="outlined">
        <Typography variant="h6" fontWeight={600} gutterBottom>Test Quote</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Subtotal (₹)" value={subtotal} onChange={e=>setSubtotal(e.target.value)} type="number" />
          </Grid>
          <Grid item xs={12} md={6} display="flex" alignItems="center">
            <Button variant="contained" onClick={requestQuote} disabled={quoteLoading} fullWidth>{quoteLoading? 'Calculating...':'Get Quote'}</Button>
          </Grid>
        </Grid>
        {computedQuote && (
          <Box mt={3}>
            <Divider sx={{ mb:2 }} />
            <Typography variant="subtitle1" fontWeight={600}>Options</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price (₹)</TableCell>
                  <TableCell>Free</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {computedQuote.options?.map((o,i)=>(
                  <TableRow key={i}>
                    <TableCell>{o.name}</TableCell>
                    <TableCell>{o.price}</TableCell>
                    <TableCell>{o.free ? 'Yes':'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Dialog open={optionModalOpen} onClose={()=>setOptionModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingOptionIndex>-1? 'Edit Shipping Option':'Add Shipping Option'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt:0.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField label="ID" fullWidth size="small" value={optionForm.id} onChange={e=>setOptionForm(f=>({...f, id:e.target.value}))} disabled={editingOptionIndex>-1} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField label="Name" fullWidth size="small" value={optionForm.name} onChange={e=>setOptionForm(f=>({...f, name:e.target.value}))} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Price (₹)" type="number" fullWidth size="small" value={optionForm.price} onChange={e=>setOptionForm(f=>({...f, price:e.target.value}))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOptionModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveOption}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminShipping;
